import { Injectable, NgZone } from '@angular/core';
import { Subject, fromEvent, merge, Subscription, timer, lastValueFrom, BehaviorSubject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { LOOKUP_KEY, LookupService } from '../lookup/lookup.service';

@Injectable({
  providedIn: 'root'
})
export class SessionInactivityService {
  // Emits when user becomes inactive
  private inactivity$ = new Subject<void>();

  // Signals teardown for all subscriptions
  private readonly destroy$: Subject<void> = new Subject<void>();

  // Timer subscription for inactivity
  private timerSub: Subscription | null = null;

  // User activity event subscription
  private activityEventsSub: Subscription | null = null;

  // private isInitialized = false;
  private isInitializedSubject = new BehaviorSubject<boolean>(false);

  // observable for subscriptions
  isInitialized$ = this.isInitializedSubject.asObservable();

  private speechInProgress = false;
  private inactivityTimeoutSec = 300; // default fallback

  constructor(private zone: NgZone, private lookupService: LookupService) { }

  get isInitialized(): boolean {
    return this.isInitializedSubject.value;
  }

  // setter
  set isInitialized(value: boolean) {
    this.isInitializedSubject.next(value);
  }

  /** Initialize on login or reload */
  async initOnLogin() {
    if (this.isInitialized) {
      console.warn('InactivityService: Already initialized.');
      return; // already set up
    }

    console.log("session init on login")
    this.isInitialized = true;

    // Fetch the inactivity config every time user logs in or reloads
    const config = await lastValueFrom(
      this.lookupService.getLookupValuesByType(LOOKUP_KEY.SESSION_TIMEOUT_IN_MIN)
    );
    const sessionTimeoutMinutes = config[0]?.lookupValue;
    this.inactivityTimeoutSec = Number(sessionTimeoutMinutes) * 60;

    // console.log("session inactivityTimeoutSec :" + this.inactivityTimeoutSec);

    this.listenToUserActivity();
    this.restoreOrStartTimer();
  }

  onReInit() {
    this.isInitialized = true;
    //set a new time
    sessionStorage.setItem('inactivityStart', Date.now().toString());
    this.listenToUserActivity();
    this.restoreOrStartTimer();
  }

  stopOnLogout() {
    this.unsubscribeEvents();
    sessionStorage.removeItem('inactivityStart');
  }

  unsubscribeEvents() {
    this.isInitialized = false;
    this.timerSub?.unsubscribe();
    this.activityEventsSub?.unsubscribe();
    this.activityEventsSub = null;
  }

  setSpeechInProgress(active: boolean) {
    this.speechInProgress = active;
    if (!active) {
      //on stopping the speech, reset the timer
      this.restartTimer();
    }
  }

  /** Event hook for inactivity */
  onInactive(callback: () => void) {
    this.inactivity$
      .pipe(takeUntil(this.destroy$))
      .subscribe(callback);
  }

  restartTimer() {
    // console.log('[InactivityTimer] Restart requested');
    sessionStorage.setItem('inactivityStart', Date.now().toString());
    this.startInactivityTimer();
  }

  private listenToUserActivity() {
    if (this.activityEventsSub) return;

    this.zone.runOutsideAngular(() => {
      const mouse$ = fromEvent(document, 'click');
      const key$ = fromEvent(document, 'keydown');
      const touch$ = fromEvent(document, 'touchstart');

      const activity$ = merge(mouse$, key$, touch$).pipe(
        filter(() => this.isInitialized)
      );

      this.activityEventsSub = activity$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.restartTimer());
    });
  }

  private restoreOrStartTimer() {
    const stored = sessionStorage.getItem('inactivityStart');
    const now = Date.now();

    if (stored) {
      // console.log("checking the stored time lines")
      const elapsedMs = now - parseInt(stored, 10);
      const remaining = this.inactivityTimeoutSec * 1000 - elapsedMs;

      if (remaining <= 0) {
        this.triggerInactivity();
        return;
      }

      this.startInactivityTimer(remaining);
    } else {
      this.restartTimer(); // starts fresh
    }
  }

  private startInactivityTimer(durationMs?: number) {
    this.timerSub?.unsubscribe();

    const timeout = durationMs ?? this.inactivityTimeoutSec * 1000;
    // console.log(`[InactivityTimer] Starting timer for ${timeout / 1000}s`);

    this.timerSub = timer(timeout)
      .pipe(
        tap(() => console.log(`[Timer] started for ${timeout}`)),
        takeUntil(this.destroy$))
      .subscribe(() => {
        console.log('[InactivityTimer] Timer expired');
        if (!this.speechInProgress && this.isInitialized) {
          this.triggerInactivity();
        }
      });
  }

  private triggerInactivity() {
    console.log("User is inactive. Triggering event.");

    this.inactivity$.next();
    this.unsubscribeEvents();
  }
}
